<template>
    <div class="page-container">
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-3" dismissible>
            {{ alertTable.message }}
        </soft-alert>

        <CrmPageHeader
            title="Localidades"
            subtitle="País → departamento → ciudad → zona → barrio"
            icon="fas fa-map-marked-alt"
            icon-bg="linear-gradient(135deg,#065F46,#10B981)"
        >
            <span v-if="!puedeEditar" class="chip chip--muted">
                <i class="fas fa-lock me-1"></i> Solo consulta
            </span>
        </CrmPageHeader>

        <div v-if="puedeEditar" class="info-box mb-4">
            <i class="fas fa-info-circle"></i>
            <div>
                El catálogo lo comparten <strong>todas las inmobiliarias</strong>: cualquier cambio los afecta a todos.
            </div>
        </div>

        <!-- Columnas en cascada -->
        <div class="cascada mb-4">
            <div v-for="nivel in columnasVisibles" :key="nivel" class="columna">
                <div class="columna__header">
                    <div class="columna__title">
                        <i :class="infoNivel(nivel).icon"></i>
                        <span>{{ infoNivel(nivel).plural }}</span>
                        <span class="columna__count">{{ formatNumber(columnas[nivel].total) }}</span>
                    </div>
                    <button
                        v-if="puedeEditar"
                        type="button"
                        class="icon-btn"
                        :title="`Nuevo(a) ${infoNivel(nivel).singular.toLowerCase()}`"
                        @click="abrirCrear(nivel)"
                    >
                        <i class="fas fa-plus"></i>
                    </button>
                </div>

                <p v-if="padreDe(nivel)" class="columna__padre">
                    en {{ seleccion[padreDe(nivel)].name }}
                </p>

                <div class="columna__filtros">
                    <input
                        v-model="columnas[nivel].q"
                        type="text"
                        class="form-control form-control-sm"
                        placeholder="Buscar nombre o id"
                        @input="buscarEnColumna(nivel)"
                    />
                    <select
                        v-if="nivel === 'paises'"
                        v-model="columnas[nivel].estado"
                        class="form-select form-select-sm mt-2"
                        @change="cargarColumna(nivel)"
                    >
                        <option value="">Todos</option>
                        <option value="activo">Activos</option>
                        <option value="inactivo">Inactivos</option>
                    </select>
                </div>

                <div class="columna__lista">
                    <button
                        v-for="item in columnas[nivel].items"
                        :key="item.id"
                        type="button"
                        class="fila"
                        :class="{ 'fila--activa': seleccion[nivel] && seleccion[nivel].id === item.id }"
                        @click="seleccionar(nivel, item)"
                    >
                        <span class="fila__nombre">
                            {{ item.name }}
                            <i
                                v-if="nivel === 'paises' && item.state === 'inactivo'"
                                class="fas fa-eye-slash fila__inactivo"
                                title="Inactivo"
                            ></i>
                        </span>
                        <span class="fila__meta">
                            <span v-if="item.portales_count" class="mini-badge mini-badge--portal" title="Portales homologados">
                                <i class="fas fa-link"></i> {{ item.portales_count }}
                            </span>
                            <span v-if="hijoDe(nivel)" class="mini-badge" :title="infoNivel(hijoDe(nivel)).plural">
                                {{ formatNumber(item.hijos_count) }}
                            </span>
                            <i v-if="hijoDe(nivel)" class="fas fa-chevron-right fila__chevron"></i>
                        </span>
                    </button>

                    <p v-if="columnas[nivel].loading" class="columna__hint">
                        <i class="fas fa-spinner fa-spin me-1"></i> Cargando…
                    </p>
                    <p v-else-if="!columnas[nivel].items.length" class="columna__hint">Sin resultados</p>
                    <button
                        v-else-if="columnas[nivel].page < columnas[nivel].lastPage"
                        type="button"
                        class="columna__mas"
                        @click="cargarMas(nivel)"
                    >
                        Cargar más ({{ columnas[nivel].items.length }} de {{ formatNumber(columnas[nivel].total) }})
                    </button>
                </div>
            </div>
        </div>

        <!-- Detalle de la localidad seleccionada -->
        <div v-if="loadingDetalle && !detalle" class="crm-card">
            <div class="crm-card__body text-center text-secondary text-sm">
                <i class="fas fa-spinner fa-spin me-1"></i> Cargando detalle…
            </div>
        </div>

        <div v-else-if="detalle" class="crm-card" :class="{ 'is-loading': loadingDetalle }">
            <div class="crm-card__body">
                <nav class="migas mb-2">
                    <template v-for="(miga, i) in detalle.ruta" :key="`${miga.nivel}-${miga.id}`">
                        <span v-if="i > 0" class="migas__sep"><i class="fas fa-chevron-right"></i></span>
                        <button
                            type="button"
                            class="migas__item"
                            :class="{ 'migas__item--actual': i === detalle.ruta.length - 1 }"
                            @click="irAMiga(i)"
                        >
                            {{ miga.name }}
                        </button>
                    </template>
                </nav>

                <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                    <div>
                        <p class="detalle__nivel">
                            <i :class="infoNivel(detalle.nivel).icon" class="me-1"></i>
                            {{ infoNivel(detalle.nivel).singular }}
                            <span class="ms-1">#{{ detalle.item.id }}</span>
                        </p>
                        <h2 class="detalle__nombre">{{ detalle.item.name }}</h2>
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                        <template v-if="puedeEditar">
                            <soft-button variant="outline" color="primary" size="sm" @click="abrirEditar">
                                <i class="fas fa-pen me-1"></i> Editar / mover
                            </soft-button>
                            <soft-button variant="outline" color="info" size="sm" @click="abrirHomologar">
                                <i class="fas fa-link me-1"></i> Homologar
                            </soft-button>
                            <soft-button variant="outline" color="danger" size="sm" @click="abrirBorrar">
                                <i class="fas fa-trash-alt me-1"></i> Eliminar
                            </soft-button>
                        </template>
                        <button type="button" class="icon-btn" title="Cerrar" @click="cerrarDetalle">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                <div class="row g-3">
                    <div v-if="extrasDetalle.length" class="col-lg-4">
                        <p class="list-label">Datos</p>
                        <dl class="datos">
                            <template v-for="extra in extrasDetalle" :key="extra.campo">
                                <dt>{{ extra.label }}</dt>
                                <dd>
                                    <span v-if="extra.campo === 'state'" class="chip" :class="extra.valor === 'activo' ? 'chip--ok' : 'chip--muted'">
                                        {{ extra.valor || '—' }}
                                    </span>
                                    <template v-else>{{ extra.valor || '—' }}</template>
                                </dd>
                            </template>
                        </dl>
                    </div>

                    <div :class="extrasDetalle.length ? 'col-lg-4' : 'col-lg-6'">
                        <p class="list-label">
                            Uso
                            <span class="chip ms-1" :class="detalle.uso && detalle.uso.total ? 'chip--warn' : 'chip--ok'">
                                {{ formatNumber(detalle.uso && detalle.uso.total) }}
                            </span>
                        </p>
                        <ul class="uso-lista">
                            <li
                                v-for="u in usoEntradas(detalle.uso)"
                                :key="u.clave"
                                :class="{ 'is-cero': !u.total }"
                            >
                                <span>{{ u.label }}</span>
                                <strong>{{ formatNumber(u.total) }}</strong>
                            </li>
                            <li v-if="!usoEntradas(detalle.uso).length" class="is-cero">
                                <span>Sin referencias</span>
                            </li>
                        </ul>
                    </div>

                    <div :class="extrasDetalle.length ? 'col-lg-4' : 'col-lg-6'">
                        <p class="list-label">Homologación en portales</p>
                        <ul class="uso-lista">
                            <li v-for="p in detalle.portales" :key="p.portal_id">
                                <span>{{ p.portal || nombrePortal(p.portal_id) }}</span>
                                <code>{{ p.codigo }}</code>
                            </li>
                            <li v-if="!(detalle.portales || []).length" class="is-cero">
                                <span>No está homologada en ningún portal</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="empty-hint">
            <i class="fas fa-mouse-pointer me-1"></i>
            Selecciona una localidad para ver su detalle, uso y homologaciones.
        </div>

        <!-- Crear / editar -->
        <soft-modal :modal-active="form.active" size="md" @close="cerrarForm">
            <template #header>
                {{ form.modo === 'crear' ? 'Nuevo(a)' : 'Editar' }}
                {{ infoNivel(form.nivel).singular.toLowerCase() }}
            </template>

            <div v-if="form.error" class="form-error mb-3">{{ form.error }}</div>

            <div class="mb-3">
                <label class="form-label">Nombre</label>
                <input
                    v-model="form.campos.name"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': form.errores.name }"
                    maxlength="191"
                    @keyup.enter="guardarForm"
                />
                <div v-if="form.errores.name" class="invalid-feedback">{{ form.errores.name }}</div>
            </div>

            <div class="row g-2">
                <div
                    v-for="campo in extrasDe(form.nivel)"
                    :key="campo"
                    :class="campo === 'descripcion' ? 'col-12' : 'col-sm-6'"
                    class="mb-2"
                >
                    <label class="form-label">{{ extraLabel(campo) }}</label>
                    <select
                        v-if="campo === 'state'"
                        v-model="form.campos.state"
                        class="form-select"
                        :class="{ 'is-invalid': form.errores.state }"
                    >
                        <option value="activo">Activo</option>
                        <option value="inactivo">Inactivo</option>
                    </select>
                    <textarea
                        v-else-if="campo === 'descripcion'"
                        v-model="form.campos.descripcion"
                        class="form-control"
                        :class="{ 'is-invalid': form.errores.descripcion }"
                        rows="2"
                    ></textarea>
                    <input
                        v-else
                        v-model="form.campos[campo]"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': form.errores[campo] }"
                    />
                    <div v-if="form.errores[campo]" class="invalid-feedback">{{ form.errores[campo] }}</div>
                </div>
            </div>

            <div v-if="padreDe(form.nivel)" class="padre-box mt-2">
                <p class="list-label mb-1">{{ infoNivel(padreDe(form.nivel)).singular }}</p>
                <div class="d-flex justify-content-between align-items-center">
                    <span class="text-sm">
                        <strong>{{ form.padreActual ? form.padreActual.name : '—' }}</strong>
                        <span v-if="form.padreActual" class="text-secondary ms-1">#{{ form.padreActual.id }}</span>
                    </span>
                    <button
                        v-if="form.modo === 'editar' && !form.moviendo"
                        type="button"
                        class="link-btn"
                        @click="form.moviendo = true"
                    >
                        <i class="fas fa-exchange-alt me-1"></i> Mover
                    </button>
                </div>

                <div v-if="form.moviendo" class="mt-3">
                    <label class="form-label">
                        Mover a otro(a) {{ infoNivel(padreDe(form.nivel)).singular.toLowerCase() }}
                    </label>
                    <LocalidadPicker
                        v-model="form.nuevoPadre"
                        :nivel="padreDe(form.nivel)"
                        :exclude-id="form.padreActual && form.padreActual.id"
                    />
                    <p class="text-xs text-secondary mt-2 mb-0">
                        <i class="fas fa-info-circle me-1"></i>
                        Se mueve con todo lo que tiene debajo.
                    </p>
                    <button type="button" class="link-btn mt-1" @click="form.moviendo = false; form.nuevoPadre = null">
                        Cancelar movimiento
                    </button>
                </div>
                <div v-if="form.errores.parent_id" class="form-error mt-2">{{ form.errores.parent_id }}</div>
            </div>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarForm">
                    Cancelar
                </soft-button>
                <soft-button variant="gradient" color="primary" size="sm" :loading="form.guardando" :disabled="form.guardando" @click="guardarForm">
                    {{ form.modo === 'crear' ? 'Crear' : 'Guardar cambios' }}
                </soft-button>
            </template>
        </soft-modal>

        <!-- Borrar / fusionar -->
        <soft-modal :modal-active="borrado.active" size="md" @close="cerrarBorrar">
            <template #header>
                <span class="text-danger"><i class="fas fa-trash-alt me-2"></i>Eliminar localidad</span>
            </template>

            <template v-if="borrado.item">
                <p class="mb-3">
                    {{ infoNivel(borrado.nivel).singular }} <strong>{{ borrado.item.name }}</strong>
                    <span class="text-secondary">#{{ borrado.item.id }}</span>
                </p>

                <p v-if="borrado.loadingUso" class="text-sm text-secondary">
                    <i class="fas fa-spinner fa-spin me-1"></i> Revisando dónde se usa…
                </p>

                <template v-else-if="borrado.uso">
                    <template v-if="enUso">
                        <div class="warn-box mb-3">
                            <i class="fas fa-exclamation-triangle"></i>
                            <div>
                                Está en uso por <strong>{{ formatNumber(borrado.uso.total) }}</strong> registro(s).
                                Para borrarla hay que <strong>fusionarla</strong>: todo lo que la usa pasa a la localidad que elijas.
                            </div>
                        </div>
                        <ul class="uso-lista mb-3">
                            <li v-for="u in usoEntradas(borrado.uso).filter((x) => x.total)" :key="u.clave">
                                <span>{{ u.label }}</span>
                                <strong>{{ formatNumber(u.total) }}</strong>
                            </li>
                        </ul>

                        <label class="form-label">Fusionar con…</label>
                        <LocalidadPicker
                            v-model="borrado.destino"
                            :nivel="borrado.nivel"
                            :parent-id="borrado.buscarEnTodos ? null : borrado.item.parent_id"
                            :exclude-id="borrado.item.id"
                        />
                        <div v-if="borrado.item.parent_id" class="form-check mt-2">
                            <input
                                id="buscarEnTodos"
                                v-model="borrado.buscarEnTodos"
                                class="form-check-input"
                                type="checkbox"
                                @change="borrado.destino = null"
                            />
                            <label class="form-check-label text-sm" for="buscarEnTodos">
                                Buscar fuera de {{ infoNivel(padreDe(borrado.nivel)).singular.toLowerCase() }} actual
                            </label>
                        </div>
                        <p class="text-xs text-secondary mt-2 mb-0">
                            Los hijos y las referencias pasan al destino. Los códigos de portal también, salvo donde el destino ya tiene el suyo.
                        </p>
                    </template>

                    <p v-else class="text-sm mb-0">
                        No está en uso. Se eliminará de forma <strong>definitiva</strong>.
                    </p>
                </template>

                <div v-if="borrado.error" class="form-error mt-3">{{ borrado.error }}</div>
            </template>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarBorrar">
                    Cancelar
                </soft-button>
                <soft-button
                    variant="gradient"
                    color="danger"
                    size="sm"
                    :loading="borrado.procesando"
                    :disabled="borrado.loadingUso || borrado.procesando || (enUso && !borrado.destino)"
                    @click="confirmarBorrar"
                >
                    <template v-if="enUso">
                        Fusionar{{ borrado.destino ? ` con ${borrado.destino.name}` : '' }} y eliminar
                    </template>
                    <template v-else>Sí, eliminar</template>
                </soft-button>
            </template>
        </soft-modal>

        <!-- Homologacion -->
        <soft-modal :modal-active="homologacion.active" size="md" @close="cerrarHomologar">
            <template #header>
                <i class="fas fa-link me-2"></i>Homologación en portales
            </template>

            <p v-if="detalle" class="text-sm text-secondary">
                Código de <strong>{{ detalle.item.name }}</strong> en cada portal. Deja vacío para quitarlo.
            </p>

            <p v-if="!portales.length" class="text-sm text-secondary mb-0">No hay portales configurados.</p>

            <div v-for="portal in portales" :key="portal.id" class="portal-row">
                <label class="portal-row__label" :for="`portal-${portal.id}`">
                    {{ portal.name }}
                    <span v-if="portal.state && portal.state !== 'active'" class="chip chip--muted ms-1">{{ portal.state }}</span>
                </label>
                <input
                    :id="`portal-${portal.id}`"
                    v-model="homologacion.codigos[portal.id]"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Sin homologar"
                />
            </div>

            <div v-if="homologacion.error" class="form-error mt-3">{{ homologacion.error }}</div>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarHomologar">
                    Cancelar
                </soft-button>
                <soft-button
                    variant="gradient"
                    color="primary"
                    size="sm"
                    :loading="homologacion.guardando"
                    :disabled="homologacion.guardando || !portales.length"
                    @click="guardarHomologacion"
                >
                    Guardar
                </soft-button>
            </template>
        </soft-modal>
    </div>
</template>

<script>
import { onMounted } from "vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import SoftModal from "@/components/SoftModal.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import LocalidadPicker from "./components/LocalidadPicker.vue";
import { useLocalidades } from "./useLocalidades.js";

export default {
    name: "Localidades",
    components: {
        SoftButton,
        SoftAlert,
        SoftModal,
        CrmPageHeader,
        LocalidadPicker,
    },
    setup() {
        const localidades = useLocalidades();
        onMounted(localidades.iniciar);
        return { ...localidades };
    },
};
</script>

<style scoped>
.info-box,
.warn-box {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 0.8rem;
    line-height: 1.45;
}

.info-box { background: #EFF6FF; border: 1px solid #BFDBFE; color: #1E3A8A; }
.warn-box { background: #FFFBEB; border: 1px solid #FDE68A; color: #92400E; }
.info-box i, .warn-box i { margin-top: 2px; }

/* Cascada */
.cascada {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 6px;
}

.columna {
    flex: 0 0 250px;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: var(--crm-card-shadow);
    overflow: hidden;
}

.columna__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 12px 4px;
}

.columna__title {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.82rem;
    font-weight: 700;
    color: #1E293B;
}

.columna__title i { color: #10B981; }

.columna__count {
    font-size: 0.68rem;
    font-weight: 600;
    color: #64748B;
    background: #F1F5F9;
    border-radius: 999px;
    padding: 1px 7px;
}

.columna__padre {
    font-size: 0.72rem;
    color: #94A3B8;
    margin: 0;
    padding: 0 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.columna__filtros { padding: 8px 12px; border-bottom: 1px solid #F1F5F9; }

.columna__lista {
    height: 380px;
    overflow-y: auto;
}

.columna__hint {
    font-size: 0.76rem;
    color: #94A3B8;
    text-align: center;
    margin: 0;
    padding: 14px 10px;
}

.columna__mas {
    width: 100%;
    background: #F8FAFC;
    border: none;
    padding: 9px;
    font-size: 0.74rem;
    font-weight: 600;
    color: #2563EB;
}

.fila {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    background: none;
    border: none;
    border-bottom: 1px solid #F8FAFC;
    padding: 8px 12px;
    text-align: left;
    font-size: 0.8rem;
    color: #334155;
}

.fila:hover { background: #F8FAFC; }
.fila--activa, .fila--activa:hover { background: #ECFDF5; color: #065F46; font-weight: 600; }

.fila__nombre {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.fila__inactivo { color: #CBD5E1; font-size: 0.68rem; margin-left: 4px; }

.fila__meta {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
}

.fila__chevron { font-size: 0.6rem; color: #CBD5E1; }

.mini-badge {
    font-size: 0.66rem;
    font-weight: 600;
    color: #64748B;
    background: #F1F5F9;
    border-radius: 6px;
    padding: 1px 6px;
}

.mini-badge--portal { color: #0369A1; background: #E0F2FE; }

.icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    background: #fff;
    color: #475569;
    font-size: 0.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.icon-btn:hover { background: #F1F5F9; }

/* Detalle */
.crm-card {
    background: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: var(--crm-card-shadow);
}

.crm-card__body { padding: 20px 22px; }
.crm-card.is-loading { opacity: 0.6; }

.migas {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
}

.migas__item {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.76rem;
    color: #2563EB;
}

.migas__item--actual { color: #64748B; font-weight: 600; }
.migas__sep { font-size: 0.55rem; color: #CBD5E1; }

.detalle__nivel {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #10B981;
    font-weight: 700;
    margin: 0 0 2px;
}

.detalle__nivel span { color: #94A3B8; }

.detalle__nombre {
    font-size: 1.2rem;
    font-weight: 700;
    color: #1E293B;
    margin: 0;
}

.list-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748B;
    margin-bottom: 8px;
}

.datos {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 6px 14px;
    font-size: 0.82rem;
    margin: 0;
}

.datos dt { color: #64748B; font-weight: 500; }
.datos dd { color: #1E293B; margin: 0; }

.uso-lista {
    list-style: none;
    padding: 0;
    margin: 0;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    overflow: hidden;
}

.uso-lista li {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 7px 12px;
    font-size: 0.8rem;
    color: #334155;
    border-bottom: 1px solid #F1F5F9;
}

.uso-lista li:last-child { border-bottom: none; }
.uso-lista li.is-cero { color: #94A3B8; }
.uso-lista code { color: #0369A1; font-size: 0.78rem; }

.empty-hint {
    text-align: center;
    padding: 28px 16px;
    font-size: 0.82rem;
    color: #94A3B8;
    border: 1px dashed #CBD5E1;
    border-radius: 12px;
}

/* Modales */
.padre-box {
    padding: 12px 14px;
    border-radius: 10px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
}

.link-btn {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.76rem;
    font-weight: 600;
    color: #2563EB;
}

.form-error {
    padding: 8px 12px;
    border-radius: 8px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    color: #B91C1C;
    font-size: 0.8rem;
}

.portal-row {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    border-bottom: 1px solid #F1F5F9;
}

.portal-row__label {
    font-size: 0.82rem;
    color: #334155;
    margin: 0;
}

.chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    background: #F1F5F9;
    color: #475569;
}

.chip--ok    { background: #ECFDF5; color: #047857; }
.chip--warn  { background: #FFFBEB; color: #B45309; }
.chip--muted { background: #F1F5F9; color: #64748B; }

@media (max-width: 576px) {
    .columna { flex-basis: 82vw; }
    .portal-row { grid-template-columns: 1fr; gap: 4px; }
}
</style>
